import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { LandingOutcome } from '@agency/contracts';
import { Badge, EmptyState, Skeleton } from '@agency/ui';
import { ApiClientError, get } from '../../lib/api.ts';
import { fmtDate, fmtDateSeconds } from '../../lib/format.ts';
import { QueryError } from '../../lib/query-state.tsx';

const EVENT_LABEL: Record<string, string> = { LeadCreated: 'Lead created', LeadAssigned: 'Owner changed', LeadQualified: 'Qualification updated', LeadProposed: 'Proposal recorded', DealUpdated: 'Deal updated', DealWon: 'Deal won', PipelineStageConfigured: 'Pipeline stage configured', LandingPublished: 'Landing published' };
type RecentEvent = { id: string; type: string; leadId: string | null; occurredAt: string; recordedAt: string };

export function OutcomesPage() {
  const outcomes = useQuery<LandingOutcome[], ApiClientError>({ queryKey: ['outcomes'], queryFn: () => get('/outcomes/landings'), refetchInterval: 15000 });
  const events = useQuery<RecentEvent[], ApiClientError>({ queryKey: ['events-recent'], queryFn: () => get('/events/recent'), refetchInterval: 15000 });
  return (
    <>
      <div className="pagehead">
        <div>
          <h1>Outcomes</h1>
          <p>Internal results by published landing version. These counts come from your own lead records; no advertising-platform data is included yet.</p>
        </div>
      </div>
      <h2 className="drawer-section" style={{ marginTop: 0 }}>By landing version</h2>
      {outcomes.isLoading ? <div role="status"><span className="ag-visually-hidden">Loading outcomes…</span><Skeleton width="100%" height={120} /></div> : outcomes.isError ? <QueryError error={outcomes.error} onRetry={() => void outcomes.refetch()} /> : outcomes.data!.length === 0 ? (
        <EmptyState title="Nothing published yet" icon="activity">Publish a landing to start collecting results per version.</EmptyState>
      ) : (
        <div className="ag-table-wrap">
          <table className="ag-table">
            <caption className="ag-visually-hidden">Lead status and won revenue by landing version</caption>
            <thead><tr><th scope="col">Landing</th><th scope="col" style={{ textAlign: 'right' }}>Leads</th><th scope="col" style={{ textAlign: 'right' }}>Qualified now</th><th scope="col" style={{ textAlign: 'right' }}>Won</th><th scope="col" style={{ textAlign: 'right' }}>Won revenue</th><th scope="col" style={{ textAlign: 'right' }}>Spam</th><th scope="col">Last lead</th></tr></thead>
            <tbody>
              {outcomes.data!.map((o) => (
                <tr key={o.landingVersionId}>
                  <td><span className="ag-cell-2"><span>{o.landingName}</span><span className="ag-mono">version {o.versionNumber}</span></span></td>
                  <td className="ag-num" style={{ textAlign: 'right' }}>{o.leads}</td>
                  <td className="ag-num" style={{ textAlign: 'right' }}>{o.qualified}</td>
                  <td className="ag-num" style={{ textAlign: 'right' }}>{o.won}</td>
                  <td className="ag-num" style={{ textAlign: 'right' }}>{o.revenue.length ? o.revenue.map((r) => `${r.amount} ${r.currency}`).join(' · ') : '—'}</td>
                  <td className="ag-num" style={{ textAlign: 'right' }}>{o.spam}</td>
                  <td>{o.lastLeadAt ? fmtDate(o.lastLeadAt) : <span style={{ color: 'var(--c-text-2)' }}>No leads yet</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <h2 className="drawer-section" style={{ marginTop: 32 }}>Recorded events</h2>
      <p style={{ color: 'var(--c-text-2)', marginBottom: 12 }}>Written by the background worker from the transactional outbox. A long gap between “occurred” and “recorded” means background processing was behind or stopped.</p>
      {events.isLoading ? <Skeleton width="100%" height={120} /> : events.isError ? <QueryError error={events.error} onRetry={() => void events.refetch()} /> : events.data!.length === 0 ? (
        <EmptyState title="No events recorded yet" icon="activity">If leads exist but no events appear, background processing is probably not running.</EmptyState>
      ) : (
        <div className="ag-table-wrap">
          <table className="ag-table ag-table--compact">
            <caption className="ag-visually-hidden">Most recent recorded events</caption>
            <thead><tr><th scope="col">Event</th><th scope="col">Lead</th><th scope="col">Occurred</th><th scope="col">Recorded</th></tr></thead>
            <tbody>
              {events.data!.map((e) => (
                <tr key={e.id}>
                  <td><Badge>{EVENT_LABEL[e.type] ?? e.type}</Badge></td>
                  <td>{e.leadId ? <Link to={`/leads?lead=${e.leadId}`} className="ag-mono">{e.leadId.slice(0, 10)}…</Link> : '—'}</td>
                  <td className="ag-num">{fmtDateSeconds(e.occurredAt)}</td>
                  <td className="ag-num">{fmtDateSeconds(e.recordedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
